import { getAuth } from "firebase/auth";
import { getUserData } from "../services/userService";
import { callPipCommand } from "./pipCommands";

// Endpoint configurations
const TIP_ENDPOINT = "https://tipplayer-5dhfwyjvzq-uc.a.run.app";
const DONATE_ENDPOINT = "https://donategoldbuff-5dhfwyjvzq-uc.a.run.app";
const COOLDOWN_MS = 24 * 60 * 60 * 1000;

const ALLOWED_PIP_COMMANDS = [
  "predict",
  "quote",
  "news",
  "holders",
  "pick",
  "value",
  "report"
];

export async function executeCommand(input, { addNotification, tab }) {
  const [command, ...args] = input.slice(1).trim().split(/\s+/);

  switch (command.toLowerCase()) {
    case "tip":
      return runTipCommand(args, addNotification);

    case "pip":
      return runPipCommand(args, addNotification, tab);

    case "donate":
      return runDonateCommand(args, addNotification);

    default:
      addNotification(`Unknown command: /${command}`, "info");
      return true;
  }
}

async function runDonateCommand(args, addNotification) {
  if (args.length === 0) {
    addNotification("Usage: /donate <amount> (e.g., /donate 100)", "info");
    return true;
  }

  const amountStr = args[0];
  const amount = parseInt(amountStr, 10);

  if (isNaN(amount) || amount <= 0) {
    addNotification("Please provide a valid whole number greater than 0.", "error");
    return true;
  }

  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      addNotification("You must be logged in to donate.", "info");
      return true;
    }

    const uid = auth.currentUser.uid;
    const token = await auth.currentUser.getIdToken();

    const res = await fetch(DONATE_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        uid,
        amount,
      }),
    });

    const data = await res.json();

    if (!res.ok || data.error) {
      addNotification(data.error || "Donation transaction failed.", "error");
      return true;
    }

    addNotification(
      `Successfully contributed $${amount.toLocaleString()} to the mint multiplier!`,
      "success"
    );

    if (data.goldBuffActivated) {
      addNotification("MINT MULTIPLIER ACTIVATED! Double gold for 24 hours beginning at 00:00.", "success");
    }

    return true;
  } catch (err) {
    addNotification(`Donation error: ${err.message}`, "error");
    return true;
  }
}

async function runTipCommand(args, addNotification) {
  if (args.length === 0) {
    addNotification("Usage: /tip <playerName>", "info");
    return true;
  }

  const recipientTip = args.join(" ").trim();
  if (!recipientTip) {
    addNotification("Recipient name required.", "info");
    return true;
  }

  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      addNotification("You must be logged in to tip.", "info");
      return true;
    }

    const uid = auth.currentUser.uid;

    const userData = await getUserData(uid);
    const lastTippedAt = userData.userTippedAt ?? 0;

    const now = Date.now();
    const availableAt = lastTippedAt + COOLDOWN_MS;

    if (now < availableAt) {
      const remaining = availableAt - now;
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      addNotification(
        `You must wait ${hours}h ${minutes}m ${seconds}s before tipping again.`,
        "info"
      );
      return true;
    }

    const token = await auth.currentUser.getIdToken();

    const res = await fetch(TIP_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        recipientTip,
        UID: uid,
      }),
    });

    const data = await res.json();
    if (!res.ok || !data.success) {
      addNotification(data.message || "Tip failed.", "error");
      return true;
    }

    addNotification(
      `You tipped ${data.recipient} $${data.tipAmount}.`,
      "success"
    );
    return true;
  } catch (err) {
    addNotification(`Error: ${err.message}`, "error");
    return true;
  }
}

async function runPipCommand(args, addNotification, tab) {
  if (args.length === 0) {
    addNotification("Usage: /pip <command> [input]", "info");
    return true;
  }

  const subCommand = args[0]?.toLowerCase();
  const input = args.slice(1).join(" ");

  if (!ALLOWED_PIP_COMMANDS.includes(subCommand)) {
    addNotification(`Invalid PIP command: ${subCommand}`, "error");
    return true;
  }

  try {
    const fullCommandString = `/pip ${subCommand}`;
    
    const data = await callPipCommand(fullCommandString, input, tab);

    if (!data.success) {
      addNotification(data.message || "PIP command failed.", "error");
    } else if (subCommand === "report") {
      addNotification("PIP report generated.", "success");
    }

    return true;
  } catch (err) {
    addNotification(`PIP error: ${err.message}`, "error");
    return true;
  }
}